import { track } from '@amplitude/analytics-browser';
import { datadogRum } from '@datadog/browser-rum';
import {
  dataDogLogger,
  DataDogLoggerActionTypes,
} from 'commons/utils/dataDogLogger';

export type AipriseEventCallback = (detail: unknown) => void | Promise<void>;

const ACTION_OWNER = 'ProofOfLifeAipriseIntegration';

function log(message: string, actionData: Record<string, unknown> = {}) {
  dataDogLogger.generate({
    actionMessage: message,
    actionOwner: ACTION_OWNER,
    actionType: DataDogLoggerActionTypes.Info,
    actionData,
  });
}

function sanitizeErrorCode(raw: unknown): string {
  if (typeof raw !== 'string') return 'unknown';
  const cleaned = raw.replaceAll(/[^A-Za-z0-9_\-:]/g, '').slice(0, 64);
  return cleaned.length > 0 ? cleaned : 'unknown';
}

function toErrorString(raw: unknown): string {
  if (raw instanceof Error) return raw.message;
  if (raw === null || raw === undefined) return '';
  if (
    typeof raw === 'string' ||
    typeof raw === 'number' ||
    typeof raw === 'boolean' ||
    typeof raw === 'bigint'
  ) {
    return String(raw);
  }
  return '[redacted]';
}

function sanitizeErrorMessage(raw: unknown): string {
  const cleaned = toErrorString(raw)
    .replaceAll(/[\u0000-\u001F\u007F]/g, ' ')
    .slice(0, 200)
    .trim();
  return cleaned.length > 0 ? cleaned : 'unknown';
}

function safeDispatch(
  callback: AipriseEventCallback,
  detail: unknown,
  source: string,
): void {
  Promise.resolve()
    .then(() => callback(detail))
    .catch((err: unknown) => {
      dataDogLogger.generate({
        actionMessage: 'AIPrise callback rejected',
        actionOwner: ACTION_OWNER,
        actionType: DataDogLoggerActionTypes.Error,
        actionData: {
          event: 'AIPriseCallbackRejected',
          source,
          errorMessage: sanitizeErrorMessage(err),
        },
      });
    });
}

export function setupAipriseEventHandlers(
  element: HTMLElement,
  onFinish: AipriseEventCallback,
  onExit: AipriseEventCallback,
  legalEntityType?: string,
): () => void {
  const handlers: Array<{ event: string; handler: (e: Event) => void }> = [
    {
      event: 'aiprise:started',
      handler: () => {
        track('pago-nube-prueba-de-vida-aiprise-usuario-comienza', {
          legal_entity_type: legalEntityType,
        });
        log('AIPrise session started', { event: 'AIPriseSessionStarted' });
      },
    },
    {
      event: 'aiprise:resumed',
      handler: () => {
        track('pago-nube-prueba-de-vida-aiprise-usuario-comienza', {
          legal_entity_type: legalEntityType,
        });
        log('AIPrise session resumed', { event: 'AIPriseSessionResumed' });
      },
    },
    {
      event: 'aiprise:successful',
      handler: (e: Event) => {
        const detail = (e as CustomEvent).detail;
        track('pago-nube-prueba-de-vida-aiprise-usuario-termina-prueba', {
          legal_entity_type: legalEntityType,
        });
        log('User finished AIPrise verification');
        safeDispatch(onFinish, detail, 'aiprise:successful');
      },
    },
    {
      event: 'aiprise:continue',
      handler: (e: Event) => {
        const detail = (e as CustomEvent).detail;
        track('pago-nube-prueba-de-vida-aiprise-usuario-continua', {
          legal_entity_type: legalEntityType,
        });
        log('User continued after AIPrise verification');
        safeDispatch(onFinish, detail, 'aiprise:continue');
      },
    },
    {
      event: 'aiprise:abandoned',
      handler: (e: Event) => {
        const detail = (e as CustomEvent).detail;
        track('pago-nube-prueba-de-vida-aiprise-usuario-sale', {
          legal_entity_type: legalEntityType,
        });
        log('User abandoned AIPrise verification');
        safeDispatch(onExit, detail, 'aiprise:abandoned');
      },
    },
    {
      event: 'aiprise:error',
      handler: (e: Event) => {
        const detail = (e as CustomEvent).detail;
        const errorCode = sanitizeErrorCode(detail?.error_code);
        datadogRum.addAction('aiprise_sdk_error', { errorCode });
        dataDogLogger.generate({
          actionMessage: 'AIPrise SDK error',
          actionOwner: ACTION_OWNER,
          actionType: DataDogLoggerActionTypes.Error,
          actionData: {
            event: 'AIPriseError',
            errorCode,
          },
        });
        safeDispatch(onExit, detail, 'aiprise:error');
      },
    },
  ];

  handlers.forEach(({ event, handler }) => {
    element.addEventListener(event, handler);
  });

  return () => {
    handlers.forEach(({ event, handler }) => {
      element.removeEventListener(event, handler);
    });
  };
}
